<template>
  <q-card
    class="bg-transparent q-mx-auto q-py-sm"
    :class="{
      'q-px-auto': $q.screen.xs,
      'column items-center q-px-sm': !$q.screen.xs
    }"
    :square="square"
    :flat="flat"
  >
    <q-card-section
      :class="{ 'q-px-sm q-py-xs': checkout }"
      :style="{
        'max-width': $register ? '100%' : checkout ? '600px' : '980px',
        'width': $q.screen.gt.xs ? '100%' : ''
      }"
    >
      <div
        v-if="!checkout || $register"
        class="row items-center justify-start"
      >
        <h2
          class="col-auto q-mx-none q-my-auto"
          :class=" {
            'q-pt-none q-pb-md': checkout && !$register,
            'q-py-none': $register
          }"
          :style="checkout || $q.screen.xs ? 'font-size: 18px;' : ''"
        >
          {{ $tc('pricing.tip', 1) }}
        </h2>
        <q-icon
          v-if="!$register && description"
          class="col-auto q-my-none"
          :class=" {
            'q-pb-md': checkout && !$register,
            'q-pb-none': $register
          }"
          name="info"
          :size="$q.screen.xs || checkout ? 'xs' : 'md'"
          color="grey"
        >
          <q-tooltip>
            <span style="white-space: pre-line;">{{ description }}</span>
          </q-tooltip>
        </q-icon>
        <div class="col" />
      </div>
      <p
        v-if="description && !$register"
        class="text-body2 q-mb-sm text-responsive-grey-8"
      >
        {{ description }}
      </p>
    </q-card-section>
    <q-card-section
      :class="{
        'q-px-sm': checkout && $q.screen.xs,
        'q-px-none': checkout && $q.screen.gt.xs,
        'q-py-xs': checkout
      }"
      class="column q-mt-none"
      :style="{
        'max-width': $register && $q.screen.gt.xs ? '440px' : checkout ? '600px' : '980px',
        'width': $q.screen.gt.xs ? '100%' : ''
      }"
    >
      <donation-tip-buttons
        :amount="tipAmount"
        :percentage="tipPercent"
        :type="tipAmountType"
        :percent-options="tipPercentOptions"
        :amount-options="tipAmountOptions"
        :order-total-without-donations-or-tips="order.totalWithoutTipAndDonation"
        :checkout="checkout"
        :is-mobile-view="$q.screen.xs"
        @update:amount="updateAmount"
        @update:percentage="updatePercentage"
      />
    </q-card-section>
  </q-card>
</template>

<script>
import { useRemoveOrderItem } from 'src/composables/order/order-items/RemoveOrderItem'
import OrderItem, { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import { DONATION_TIP_TYPES } from 'src/models/site/Site'
import DonationTipButtons from 'src/modules/checkout/components/DonationTipButtons'

export default {
  name: 'TipWidget',
  configuration: ['currentCircuit', 'currentSite'],
  components: {
    DonationTipButtons
  },
  inject: ['order'],
  props: {
    title: {
      type: String,
      required: false,
      default: null
    },
    description: {
      type: String,
      required: false,
      default: null
    },
    buttonText: {
      type: String,
      required: false,
      default: null
    },
    checkout: {
      type: Boolean,
      required: false,
      default: false
    },
    square: {
      type: Boolean,
      required: false,
      default: false
    },
    flat: {
      type: Boolean,
      required: false,
      default: false
    },
    demoMode: {
      type: Boolean,
      required: false,
      default: false
    },
    overrideAmountTypes: {
      type: Array,
      required: false,
      default () {
        return [
          DONATION_TIP_TYPES.AMOUNT,
          DONATION_TIP_TYPES.PERCENT]
      }
    },
    defaultPercent: {
      type: Number,
      required: false,
      default: 0
    },
    defaultAmount: {
      type: Number,
      required: false,
      default: 0
    },
    preselectedValue: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup () {
    const { removeOrderItem } = useRemoveOrderItem()
    return {
      removeOrderItem
    }
  },
  data () {
    return {
      orderItem: null,
      tipAmountType: this.overrideAmountTypes[0],
      tipAmount: 0,
      tipPercent: 0,
      defaultTitle: null,
      defaultButtonText: 'Tip'
    }
  },
  computed: {
    tipAmountOptions () {
      let options = [
        { label: this.$i18n.n(1, 'currency'), value: 1 },
        { label: this.$i18n.n(2, 'currency'), value: 2 },
        { label: this.$i18n.n(5, 'currency'), value: 5 }
      ]

      if (this.defaultAmount && !_.some(options, option => option.value === this.defaultAmount)) {
        options.push({ label: this.$i18n.n(this.defaultAmount, 'currency'), value: this.defaultAmount })
        options = _.sortBy(options, option => +option.value)
      }

      return options
    },
    tipPercentOptions () {
      let percentValues = null
      const requestReceiptTipsEnabled = this.currentSite.receiptPrintingOptions?.thermal_receipt_print_tip_options
      const receiptTipValues = this.currentSite.receiptPrintingOptions?.thermal_receipt_tip_options
      const shouldUseReceiptTipValues = requestReceiptTipsEnabled && receiptTipValues?.length !== 0
      if (shouldUseReceiptTipValues) {
        percentValues = receiptTipValues
      } else {
        percentValues = this.$q.screen.gt.xs ? [10, 15, 20, 25] : [15, 20, 25]
      }

      let options = [
        ...percentValues.map(value => ({ label: `${value}%`, value, slot: `${value}` }))
      ]

      if (this.defaultPercent && !_.some(options, option => option.value === this.defaultPercent)) {
        options.push({ label: `${this.defaultPercent}%`, value: this.defaultPercent, slot: `${this.defaultPercent}` })
        options = _.sortBy(options, option => +option.value)
      }

      return options
    },
    toggleTextColor () {
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    }
  },
  watch: {
    tipAmount () {
      this.updateTip()
    },
    'order.loading': {
      handler () {
        this.initializeTip()
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { DONATION_TIP_TYPES })
    if (this.preselectedValue) {
      if (this.tipAmountType === DONATION_TIP_TYPES.AMOUNT) {
        this.tipAmount = this.defaultAmount
        this.tipPercent = this.computePercentFromAmount(this.defaultAmount)
      } else {
        this.tipPercent = this.defaultPercent
        this.tipAmount = this.computeAmountFromPercent(this.defaultPercent)
      }
    }
  },
  unmounted () {
    this.stopWatch && this.stopWatch()

    const tip = this.getTipOrderItem()

    if (tip?.amount === 0) {
      this.removeTipOrderItem(tip)
    }
  },
  methods: {
    getTipOrderItem () {
      this.orderItem = _.find(this.order.orderItems.data, { type: ORDER_ITEM_TYPES.TIP, state: 'draft' })

      if (_.isNil(this.orderItem)) {
        this.orderItem = new OrderItem()
        this.orderItem.type = ORDER_ITEM_TYPES.TIP
        this.orderItem.amount = 0

        this.order.orderItems.add(this.orderItem)
      }

      return this.orderItem
    },
    removeTipOrderItem (tip) {
      if (this.removeOrderItem(tip)) {
        this.orderItem = null
      }
    },
    updateTip () {
      let tip = this.getTipOrderItem()
      const value = this.tipAmount
      if (value <= 0 && !_.isNil(tip)) {
        this.removeTipOrderItem(tip)
        this.resetTipType()
        return
      } else if (value > 0 && _.isNil(tip)) {
        tip = this.getTipOrderItem()
        this.order.orderItems.add(tip)
      }

      if (value > 0) {
        tip.amount = value
      }
    },
    resetTipType () {
      const tip = this.orderItem

      if (this.overrideAmountTypes.length === 1) {
        this.tipAmountType = this.overrideAmountTypes[0]
        return
      }

      this.tipAmountType = this.order.totalWithoutTipAndDonation > 10 && (
        _.isNil(tip) || tip.amount === 0 || _.includes([10, 15, 20, 25], this.computePercentFromAmount(tip.amount))
      )
        ? DONATION_TIP_TYPES.PERCENT
        : DONATION_TIP_TYPES.AMOUNT
    },
    async initializeTip () {
      if (!this.demoMode && this.order.isRemote && !this.order.loaded) {
        await this.order.ensureLoaded()
      }

      let tip = this.getTipOrderItem()

      if (tip?.amount === 0) {
        this.removeTipOrderItem(tip)
        tip = null
      }

      this.resetTipType()

      if (!_.isNil(tip)) {
        const amount = tip.amount
        this.tipAmount = amount
        this.tipPercent = this.computePercentFromAmount(amount)
      }
    },
    computeAmountFromPercent (percent) {
      return Math.round(this.order.totalWithoutTipAndDonation * percent) / 100.0
    },
    computePercentFromAmount (amount) {
      if (this.order.totalWithoutTipAndDonation <= 0) return 0

      return Math.round((amount / this.order.totalWithoutTipAndDonation) * 100.0)
    },
    tip () {
      if (this.demoMode) return

      this.$router.push('/checkout/cart')
    },
    updateAmount (value) {
      this.tipAmount = value
    },
    updatePercentage (value) {
      this.tipPercent = value
    }
  }

}

</script>

<style lang="scss" scoped>
h2 {
  color: var(--q-primary);
  font-weight: bold;
  line-height: 1em;
}
</style>
